/**
 * MANAGER DASHBOARD - Team Performance Dashboard
 * ===============================================
 * Same structure as City Head but auto-filtered by manager_id
 * Focused, Personal, Performance-oriented
 * 
 * Sections:
 * 1. Today Focus (Team Only)
 * 2. Rep-wise Funnel Health
 * 3. Referral Performance (Team)
 * 4. VDNB Recovery (Team)
 * 5. All Bookings (Filtered)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Calendar, Users, Building2, IndianRupee, TrendingUp, TrendingDown,
  Clock, Target, Award, AlertTriangle, ArrowRight, RefreshCw,
  ChevronRight, Phone, UserCheck, UserX, Percent, 
  BarChart3, User
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import UnifiedBookingTable from '../components/UnifiedBookingTable';
import LostReviewPool from '../components/LostReviewPool';

// === METRIC CARD COMPONENT ===
const MetricCard = ({ 
  label, 
  value, 
  icon: Icon, 
  color = 'slate', 
  onClick, 
  trend,
  suffix = '',
  clickable = false 
}) => {
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    cyan: 'bg-cyan-50 text-cyan-700',
    red: 'bg-red-50 text-red-700',
  };
  
  return (
    <div 
      className={`p-4 rounded-xl border ${colorClasses[color]} ${clickable ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-white/80`}>
          <Icon size={18} className="text-current" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold">{value}{suffix}</p>
      <p className="text-xs text-current/70 mt-1">{label}</p>
      {clickable && (
        <p className="text-xs text-current/50 mt-2 flex items-center gap-1">
          Click to view <ChevronRight size={12} />
        </p>
      )}
    </div>
  );
};

// === REP FUNNEL CARD COMPONENT ===
const RepFunnelCard = ({ rep, onClick }) => {
  const conversionColor = rep.visit_to_booking_pct >= 10 ? 'text-emerald-600' : 
                          rep.visit_to_booking_pct >= 5 ? 'text-amber-600' : 'text-red-600';
  
  return (
    <Card 
      className="p-4 hover:shadow-md transition-all cursor-pointer border-l-4 border-l-slate-300 hover:border-l-emerald-500"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <User size={18} className="text-slate-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{rep.rep_name}</h3>
            <Badge variant="outline" className={conversionColor}>
              {rep.visit_to_booking_pct}% conv
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-lg font-bold text-slate-700">{rep.unique_customers}</p>
          <p className="text-[10px] text-slate-500">Customers</p>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <p className="text-lg font-bold text-blue-700">{rep.first_visits}</p>
          <p className="text-[10px] text-slate-500">1st Visits</p>
        </div>
        <div className="text-center p-2 bg-emerald-50 rounded-lg">
          <p className="text-lg font-bold text-emerald-700">{rep.bookings}</p>
          <p className="text-[10px] text-slate-500">Bookings</p>
        </div>
        <div className="text-center p-2 bg-purple-50 rounded-lg">
          <p className="text-lg font-bold text-purple-700">₹{rep.net_av_lacs}L</p>
          <p className="text-[10px] text-slate-500">Net AV</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">
          VDNB: <span className={`font-medium ${rep.vdnb_pct > 30 ? 'text-red-600' : 'text-slate-700'}`}>
            {rep.vdnb_pct}%
          </span>
        </span>
        <span className="text-slate-500">
          Referrals: <span className="font-medium text-purple-700">{rep.referral_bookings}</span>
        </span>
      </div>
    </Card>
  );
};

// === MAIN COMPONENT ===
const ManagerDashboard = ({ user }) => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [period, setPeriod] = useState('mtd');
  const [activeSection, setActiveSection] = useState('overview');
  const [lostReviewCount, setLostReviewCount] = useState(0);
  
  // Fetch Lost Review count
  const fetchLostReviewCount = useCallback(async () => {
    try {
      const response = await api.getLostReviewPool({ limit: 1 });
      setLostReviewCount(response.data?.total || 0);
    } catch (error) {
      // Manager may not have access, that's ok
      console.log('Lost review not available for this role');
    }
  }, []);
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      const response = await api.getManagerDashboard(params);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period]);
  
  useEffect(() => {
    fetchDashboard();
    fetchLostReviewCount();
  }, [fetchDashboard, fetchLostReviewCount]);
  
  if (loading || !dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-slate-500">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          Loading dashboard...
        </div>
      </div>
    );
  }
  
  const { today_focus, rep_funnel_health, referral_performance, vdnb_recovery } = dashboardData;
  
  // Calculate team totals
  const teamTotals = {
    bookings: rep_funnel_health?.reduce((sum, r) => sum + r.bookings, 0) || 0,
    netAv: rep_funnel_health?.reduce((sum, r) => sum + r.net_av_lacs, 0) || 0,
    customers: rep_funnel_health?.reduce((sum, r) => sum + r.unique_customers, 0) || 0,
    firstVisits: rep_funnel_health?.reduce((sum, r) => sum + r.first_visits, 0) || 0,
  };
  
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header - Personal & Focused */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Team Performance</h1>
          <p className="text-slate-500">
            {dashboardData.manager_name} • {dashboardData.team_size} team members
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px]">
              <Calendar size={14} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="mtd">MTD</SelectItem>
              <SelectItem value="qtd">QTD</SelectItem>
              <SelectItem value="ytd">YTD</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={fetchDashboard}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>
      
      {/* Team Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
          <p className="text-3xl font-bold text-emerald-700">{teamTotals.bookings}</p>
          <p className="text-sm text-emerald-600">Team Bookings</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
          <p className="text-3xl font-bold text-purple-700">₹{teamTotals.netAv.toFixed(1)}L</p>
          <p className="text-sm text-purple-600">Team Net AV</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
          <p className="text-3xl font-bold text-blue-700">{teamTotals.firstVisits}</p>
          <p className="text-sm text-blue-600">First Visits</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
          <p className="text-3xl font-bold text-slate-700">{teamTotals.customers}</p>
          <p className="text-sm text-slate-600">Active Customers</p>
        </div>
      </div>
      
      {/* Section Tabs - Manager Focused */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {[
          { key: 'overview', label: 'Today Focus', icon: Clock },
          { key: 'reps', label: 'Rep Performance', icon: Users },
          { key: 'referrals', label: 'Referrals', icon: Award },
          { key: 'vdnb', label: 'VDNB', icon: AlertTriangle },
          { key: 'bookings', label: 'Team Bookings', icon: Building2 },
          { key: 'lost_review', label: 'Lost Review', icon: UserX, badge: lostReviewCount },
        ].map(tab => (
          <Button
            key={tab.key}
            variant={activeSection === tab.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection(tab.key)}
            className={`${activeSection === tab.key ? 'bg-slate-900' : ''} relative`}
          >
            <tab.icon size={14} className="mr-1" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {tab.badge}
              </span>
            )}
          </Button>
        ))}
      </div>
      
      {/* ===================== SECTION 1: TODAY FOCUS ===================== */}
      {activeSection === 'overview' && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={18} className="text-amber-700" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Today's Activity</h2>
              <p className="text-xs text-slate-500">Your team's real-time metrics</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard
              label="Visits Scheduled"
              value={today_focus?.visits_scheduled_today || 0}
              icon={Calendar}
              color="blue"
              clickable
            />
            <MetricCard
              label="Visits Completed"
              value={today_focus?.visits_completed_today || 0}
              icon={UserCheck}
              color="emerald"
              clickable
            />
            <MetricCard
              label="Revisits"
              value={today_focus?.revisits_today || 0}
              icon={RefreshCw}
              color="cyan"
              clickable
            />
            <MetricCard
              label="Bookings Today"
              value={today_focus?.bookings_today || 0}
              icon={Building2}
              color="purple"
              clickable
            />
          </div>
        </Card>
      )}
      
      {/* ===================== SECTION 2: REP FUNNEL HEALTH ===================== */}
      {activeSection === 'reps' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Rep Performance</h2>
            <Badge variant="outline">{rep_funnel_health?.length || 0} reps</Badge>
          </div>
          
          {rep_funnel_health?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rep_funnel_health.map(rep => (
                <RepFunnelCard
                  key={rep.rep_id}
                  rep={rep}
                  onClick={() => navigate(`/crm?rep=${rep.rep_id}`)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Users size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No team members found</p>
              <p className="text-sm text-slate-400">Reps will appear here once assigned to your team</p>
            </Card>
          )}
        </div>
      )}
      
      {/* ===================== SECTION 3: REFERRALS ===================== */}
      {activeSection === 'referrals' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Team Referral Performance</h2>
          
          <Card className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <MetricCard
                label="Referral Bookings"
                value={referral_performance?.referral_bookings || 0}
                icon={Award}
                color="purple"
              />
              <MetricCard
                label="Referral AV"
                value={`₹${referral_performance?.referral_net_av_lacs || 0}`}
                suffix="L"
                icon={IndianRupee}
                color="emerald"
              />
              <MetricCard
                label="Contribution to Team"
                value={teamTotals.bookings > 0 
                  ? Math.round((referral_performance?.referral_bookings || 0) / teamTotals.bookings * 100)
                  : 0
                }
                suffix="%"
                icon={Percent}
                color="amber"
              />
            </div>
          </Card>
          
          {/* Rep-wise referral breakdown */}
          <Card className="p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Rep-wise Referrals</h3>
            <div className="space-y-2">
              {rep_funnel_health?.filter(r => r.referral_bookings > 0).map(rep => (
                <div key={rep.rep_id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="font-medium text-slate-700">{rep.rep_name}</span>
                  <Badge className="bg-purple-100 text-purple-700">{rep.referral_bookings} referrals</Badge>
                </div>
              ))}
              {!rep_funnel_health?.some(r => r.referral_bookings > 0) && (
                <p className="text-center text-slate-400 py-4">No referral bookings yet</p>
              )}
            </div>
          </Card>
        </div>
      )}
      
      {/* ===================== SECTION 4: VDNB ===================== */}
      {activeSection === 'vdnb' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Team VDNB Status</h2>
          
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-amber-700">{vdnb_recovery?.total_vdnb || 0}</p>
                <p className="text-sm text-slate-600">Total VDNB Leads</p>
              </div>
              <div className="p-4 bg-amber-100 rounded-full">
                <AlertTriangle size={32} className="text-amber-700" />
              </div>
            </div>
          </Card>
          
          {/* Rep-wise VDNB */}
          <Card className="p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Rep-wise VDNB %</h3>
            <div className="space-y-2">
              {rep_funnel_health?.map(rep => (
                <div key={rep.rep_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{rep.rep_name}</span>
                  <span className={`font-bold ${
                    rep.vdnb_pct > 30 ? 'text-red-600' : 
                    rep.vdnb_pct > 20 ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {rep.vdnb_pct}%
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
      
      {/* ===================== SECTION 5: TEAM BOOKINGS ===================== */}
      {activeSection === 'bookings' && (
        <UnifiedBookingTable
          role="manager"
          title="Team Bookings"
          defaultFilters={{ period }}
          showSummaryStrip={true}
          pageSize={20}
        />
      )}
      
      {/* ===================== SECTION 6: LOST REVIEW POOL ===================== */}
      {activeSection === 'lost_review' && (
        <LostReviewPool
          userCity={user?.city}
          onActionComplete={() => {
            fetchLostReviewCount();
            toast.success('Action completed');
          }}
        />
      )}
    </div>
  );
};

export default ManagerDashboard;
